import { Link } from "react-router-dom";
import { LogOut, MessagesSquare, ArrowLeft, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";

export default function Navbar({ backTo, title, subtitle, roleLabel, globalPercent, onOpenChat, cover }) {
  const { user, logout } = useAuth();
  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="border-b border-border/70 bg-background/85 backdrop-blur-md sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {backTo && (
            <Button asChild variant="ghost" size="icon" className="rounded-full shrink-0 -ml-2">
              <Link to={backTo} data-testid="navbar-back-link" aria-label="Volver">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
          )}
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="navbar-logo-link">
            <img src="/mori-logo.jpg" alt="Mori" className="h-9 w-9 rounded-lg object-cover" />
            {!title && <span className="text-xl font-bold tracking-tight lowercase">mori</span>}
          </Link>
          {title && cover && (
            <img
              src={`${API}/files/${cover}`}
              alt="Portada"
              title="Portada"
              className="h-10 w-16 rounded-md object-cover border border-border shrink-0 hidden sm:block"
              data-testid="navbar-cover"
            />
          )}
          {title && (
            <div className="min-w-0 border-l border-border pl-3">
              <div className="font-semibold tracking-tight truncate leading-tight" data-testid="navbar-title">{title}</div>
              {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
            </div>
          )}
          {roleLabel && (
            <Badge variant="secondary" className="hidden md:inline-flex font-mono text-[10px] uppercase tracking-wider" data-testid="navbar-role-badge">
              {roleLabel}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-3">
          {typeof globalPercent === "number" && (
            <div className="hidden sm:flex items-center gap-2" data-testid="navbar-global-progress">
              <span className="caption">Avance</span>
              <div className="h-2 w-28 rounded-full bg-secondary overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-700"
                  style={{ width: `${globalPercent}%`, transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
                />
              </div>
              <span className="font-mono text-sm font-semibold">{globalPercent}%</span>
            </div>
          )}

          {onOpenChat && (
            <Button data-testid="navbar-chat-button" variant="outline" size="sm" className="rounded-full gap-2" onClick={onOpenChat}>
              <MessagesSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Preguntar a Mori</span>
            </Button>
          )}

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-button" className="rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-9 w-9 border border-border">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-accent text-olive text-sm font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium truncate">{user?.name}</span>
                    <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/" data-testid="user-menu-home">
                    <LayoutGrid className="h-4 w-4 mr-2" /> Mis proyectos
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
